import { FC, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, HStack, Link, RefreshOutline, rem, Spinner, Text, XOutline } from '@bonusly/design-system';

import { PromptChips } from '../../../../prompt-chips/prompt-chips';
import type { CommentAssistMode } from './hooks/use-bizy-comment-suggestions';

export type BizyCommentSuggestionsProps = {
	bonusId: string;
	suggestions: string[];
	isLoading: boolean;
	isError: boolean;
	/** Cold ideas vs draft refine — drives heading + analytics. */
	mode?: CommentAssistMode;
	/** Layout variant: `web` (padded, "Close suggestions") or `mobile` (no padding, "Close"). */
	variant?: 'web' | 'mobile';
	onSelect: (text: string, index: number) => void;
	onRegenerate: () => void;
	onClear: () => void;
};

/** Figma UI 1 / Normal — Inter at the size-1 ramp (0.75rem), weight 400. */
const UI1_NORMAL = {
	fontFamily: 'ui',
	fontSize: 1,
	fontWeight: 'normal',
} as const;

const AssistTextLink: FC<{
	onClick: () => void;
	testId: string;
	children: ReactNode;
}> = ({ onClick, testId, children }) => (
	<Link
		as="button"
		type="button"
		display="inline-flex"
		alignItems="center"
		gap={1}
		{...UI1_NORMAL}
		color="text.softer"
		textDecoration="none"
		_hover={{ textDecoration: 'underline' }}
		cursor="pointer"
		onClick={onClick}
		data-testid={testId}
	>
		{children}
	</Link>
);

/**
 * In-box picker for Bizy comment suggestions (ideas / refine).
 * Sits 8px under the editor (box-to-box), with content padding 8/40/8/4.
 */
export const BizyCommentSuggestions: FC<BizyCommentSuggestionsProps> = ({
	bonusId,
	suggestions,
	isLoading,
	isError,
	mode = 'ideas',
	variant = 'web',
	onSelect,
	onRegenerate,
	onClear,
}) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'comment.bizy_assist' });
	const { t: tCommon } = useTranslation('common');
	const { track } = useAnalytics();

	const isMobileVariant = variant === 'mobile';
	const hasResults = !isLoading && !isError && suggestions.length > 0;
	const showRegenerate = hasResults || isError;
	// Close only after the request settles — spinner should be the sole chrome
	// while we're waiting so the row doesn't look half-baked.
	const showClose = !isLoading;
	const isRefine = mode === 'refine';

	const trackedBatchKey = useRef<string | null>(null);
	useEffect(() => {
		if (!hasResults) return;
		const key = `${mode}:${suggestions.join('\0')}`;
		if (trackedBatchKey.current === key) return;
		trackedBatchKey.current = key;
		track(AnalyticsEvent.CommentAssist.SuggestionsShown, {
			bonusId,
			suggestionsCount: suggestions.length,
			mode,
		});
	}, [hasResults, suggestions, track, bonusId, mode]);

	const handleRegenerate = () => {
		track(AnalyticsEvent.CommentAssist.SuggestionsRegenerated, { bonusId, mode });
		onRegenerate();
	};

	const handleClear = () => {
		track(AnalyticsEvent.CommentAssist.SuggestionsCleared, { bonusId, mode });
		onClear();
	};

	const handleSelect = (text: string, index: number) => {
		track(isRefine ? AnalyticsEvent.CommentAssist.RefineSuggestionChosen : AnalyticsEvent.CommentAssist.SuggestionChosen, {
			bonusId,
			suggestionIndex: index,
			suggestionsCount: suggestions.length,
			mode,
		});
		onSelect(text, index);
	};

	return (
		<Box
			data-testid="bizy-comment-suggestions"
			w="full"
			mt="8px"
			pt={isMobileVariant ? 0 : '8px'}
			pb={isMobileVariant ? 0 : '8px'}
			pl={isMobileVariant ? 0 : '4px'}
			pr={isMobileVariant ? 0 : '40px'}
		>
			<HStack justify="space-between" align="center" mb={hasResults || isLoading || isError ? 1.5 : 0} gap={2}>
				<Text {...UI1_NORMAL} color="text.softer" flex="1" minW={0}>
					{t(isRefine ? 'add_something_like' : 'try_something_like')}
				</Text>
				{(showRegenerate || showClose) && (
					<HStack spacing={3} flexShrink={0}>
						{showRegenerate && (
							<AssistTextLink onClick={handleRegenerate} testId="bizy-comment-suggestions-regenerate">
								<RefreshOutline boxSize={4} />
								{t('regenerate')}
							</AssistTextLink>
						)}
						{showClose && (
							<AssistTextLink onClick={handleClear} testId="bizy-comment-suggestions-clear">
								<XOutline boxSize={4} />
								{isMobileVariant ? tCommon('close') : t('close_suggestions')}
							</AssistTextLink>
						)}
					</HStack>
				)}
			</HStack>

			{isLoading ? (
				<Spinner boxSize={rem(20)} color="gray.300" data-testid="bizy-comment-suggestions-spinner" />
			) : isError ? (
				<Text fontSize="sm" color="text.softer">
					{t(isRefine ? 'refine_error' : 'error')}
				</Text>
			) : (
				<Box mb={0} sx={{ '& > *': { mb: '0 !important' } }}>
					<PromptChips fontSize="sm" items={suggestions.map((prompt) => ({ prompt }))} onSelectPrompt={handleSelect} layout="vertical" />
				</Box>
			)}
		</Box>
	);
};
